import { isChromeBrowser } from 'client/services/managedBrowserService';
import ConfigService from 'client/util/initializationHandler/ConfigService';
import api from 'client/api';

export const ISLAND_VERIFICATION_MESSAGE_TYPE = 'JUMPCLOUD_ISLAND_VERIFICATION';

// Upper bound on how long a caller awaiting device trust will block. Matches the 5s
// managedBrowserService allows for Chrome so waitForDeviceTrustValidation has a single
// predictable worst case; callers embedded in a native webview (the Windows SSAP credential
// provider) abandon the page well before that. Island fails closed on expiry, so a challenge
// round trip slower than this rejects a device that would otherwise have verified.
export const ISLAND_VALIDATION_TIMEOUT_MS = 5000;

const getIframeOrigin = () => {
  const url = new URL(ConfigService.islandVerifiedAccessUrl as string);
  return url.origin;
};

const sendJWT = (token: string) => api.user.validateIslandAccess(token);

function isIslandVerificationMessage(parsedData: Record<string, unknown>) {
  if (parsedData.type === ISLAND_VERIFICATION_MESSAGE_TYPE) {
    return true;
  }

  // island-va currently posts { token } / { error } without a type field
  return typeof parsedData.token === 'string' || typeof parsedData.error === 'string';
}

export class IslandBrowserService {
  private iframeId = 'islandBrowserVerificationIframe';

  loadIframe(): void {
    const iframe = document.createElement('iframe');
    iframe.src = ConfigService.islandVerifiedAccessUrl as string;
    iframe.width = '1';
    iframe.height = '1';
    iframe.style.position = 'absolute';
    iframe.style.left = '-9999px';
    iframe.style.opacity = '0';
    iframe.id = this.iframeId;

    document.body.appendChild(iframe);
  }

  private removeIframe() {
    const element = document.getElementById(this.iframeId);
    if (element) {
      element.outerHTML = '';
    }
  }

  private createMessageListener(
    resolve: (value: string) => void,
    reject: (reason: unknown) => void,
  ) {
    const listener = async (message: { origin: string; data: unknown; }) => {
      if (![getIframeOrigin(), ''].includes(message.origin)) {
        return;
      }

      let parsedData: Record<string, unknown>;
      try {
        parsedData = typeof message.data === 'string'
          ? JSON.parse(message.data)
          : message.data as Record<string, unknown>;
      } catch (e) {
        return;
      }

      if (!isIslandVerificationMessage(parsedData)) {
        return;
      }

      window.removeEventListener('message', listener);
      this.removeIframe();

      try {
        const { token, error } = parsedData;
        if (typeof token === 'string') {
          await sendJWT(token);
          resolve('success');
        } else if (typeof error === 'string') {
          reject(new Error(error));
        } else {
          reject(new Error('No token or error in message'));
        }
      } catch (e) {
        reject(e);
      }
    };

    window.addEventListener('message', listener);
  }

  validateIsland(): Promise<string> {
    if (!ConfigService.islandVerifiedAccessUrl) {
      return Promise.reject(new Error('validate island: missing islandVerifiedAccessUrl'));
    }

    const timeoutPromise = new Promise<string>((resolve, reject) => {
      setTimeout(() => reject(new Error('validate island: timeout')), ISLAND_VALIDATION_TIMEOUT_MS);
    });
    // Island Enterprise Browser reports a stock Chrome user agent and Chrome/Chromium UA-CH
    // brands, so it cannot be detected client side. The flow discriminator is the
    // X-Device-Trust: IslandVerifiedDeviceAccess request header the browser adds itself; island-va
    // fails closed for every other browser and posts an error back through the iframe.
    //
    // Attaching the iframe is still gated on a Google Chrome UA-CH brand, which Island reports
    // and the credential provider's CEF viewer (JCLoginViewer) does not. CEF crashes its renderer
    // on this cross-origin iframe, taking the SSAP login window down with it.
    const verifyPromise = new Promise<string>((resolve, reject) => {
      if (!isChromeBrowser()) {
        reject(new Error('validate island: not a Chrome browser'));
        return;
      }

      this.createMessageListener(resolve, reject);
      this.loadIframe();
    });

    return Promise.race([timeoutPromise, verifyPromise]);
  }
}

export default new IslandBrowserService();
